<!--
  Client Portal v2: a tiny sparkline for the "Your stores" leaderboard.

  RENDERING (PR D5): a LINE + gradient AREA sparkline, matching the v2 design
  primitive (planning/.../design/primitives.jsx -> Sparkline). The line is a
  brand-colored SVG path (M/L segments); a closed area path fills the region
  beneath it with a soft top-to-bottom gradient. This replaces the PR D2/D3
  vertical-bars rendering -- the design system uses a line/area sparkline for
  the per-store trend.

  SCALING: SELF-scaled by default (PR D3 behavior preserved). Each store's
  sparkline is scaled against THAT STORE's own min..max, so every store's line
  uses the full height of the cell -- the sparkline conveys the store's own
  TREND while the adjacent dollar figure conveys MAGNITUDE.

  ``sharedMax`` is still honored when a caller explicitly passes a positive
  value (cross-store shared denominator). When it is omitted/zero the component
  self-scales against this store's own peak.
-->
<template>
  <div class="store-spark" :data-testid="testid" :aria-label="ariaLabel">
    <svg
      class="store-spark__svg"
      :viewBox="`0 0 ${width} ${height}`"
      preserveAspectRatio="none"
      role="img"
      :data-testid="`${testid}-svg`"
    >
      <defs>
        <linearGradient
          :id="gradientId"
          x1="0"
          x2="0"
          y1="0"
          y2="1"
        >
          <stop offset="0" :stop-color="strokeColor" stop-opacity="0.22" />
          <stop offset="1" :stop-color="strokeColor" stop-opacity="0" />
        </linearGradient>
      </defs>
      <!-- Gradient AREA fill beneath the line (closed to the baseline). -->
      <path
        class="store-spark__area"
        :data-testid="`${testid}-area`"
        :d="areaPath"
        :fill="`url(#${gradientId})`"
        stroke="none"
      />
      <!-- The trend LINE itself (brand-colored, ~1.8 stroke). -->
      <path
        class="store-spark__line"
        :data-testid="`${testid}-line`"
        :d="linePath"
        fill="none"
        :stroke="strokeColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
</template>

<script>
// Viewbox geometry. The SVG scales to the cell via preserveAspectRatio=none;
// these are the internal coordinate units the path is computed in. A 2px top/
// bottom inset keeps the peak/trough stroke from clipping at the edges.
const VIEW_W = 116;
const VIEW_H = 30;
const INSET = 2;

export default {
  name: "StoreSparkline",
  props: {
    // Per-bucket values for THIS store (e.g. orders or spend per period).
    values: {
      type: Array,
      default: () => [],
    },
    // Optional cross-store denominator. When > 0 the line is scaled against it
    // (shared-scale mode); when omitted/0 it SELF-scales against this store's
    // own min..max (the default).
    sharedMax: {
      type: Number,
      default: 0,
    },
    testid: {
      type: String,
      default: "store-sparkline",
    },
  },
  data() {
    return {
      width: VIEW_W,
      height: VIEW_H,
    };
  },
  computed: {
    // Coerce every value to a finite number once; downstream math reuses this.
    numeric() {
      return this.values.map((v) => Number(v || 0));
    },
    peak() {
      return Math.max(0, ...this.numeric);
    },
    ariaLabel() {
      return `activity sparkline, peak ${this.peak}`;
    },
    strokeColor() {
      // Brand-colored stroke (matches the v2 --chart-1 / --tf-primary token).
      return "var(--tf-primary, #119ae9)";
    },
    // A stable gradient id derived from the per-instance testid so multiple
    // sparklines on one page don't collide on a shared <linearGradient> id.
    // The Overview leaderboard passes a unique testid per store row.
    gradientId() {
      return `store-spark-grad-${this.testid}`;
    },
    // The [x, y] points for the line, mapped into the viewBox. Y is inverted
    // (SVG origin is top-left) and inset by INSET on both ends so the stroke
    // never clips. Self-scaled against this store's own min..max unless a
    // positive sharedMax is supplied (then 0..sharedMax).
    points() {
      const vals = this.numeric;
      const n = vals.length;
      if (n === 0) return [];

      let min;
      let max;
      if (this.sharedMax > 0) {
        // Shared-scale mode: measure every store against a common 0..sharedMax
        // denominator so magnitudes are comparable across stores.
        min = 0;
        max = this.sharedMax;
      } else {
        // Self-scale mode: this store fills its own cell (own min..max).
        min = Math.min(...vals);
        max = Math.max(...vals);
      }
      const range = max - min || 1;
      const usableH = this.height - INSET * 2;

      // A single-point store still needs a valid x; place it at the left edge.
      const denom = n > 1 ? n - 1 : 1;
      return vals.map((v, i) => {
        const x = (i / denom) * this.width;
        const y = this.height - INSET - ((v - min) / range) * usableH;
        return [x, y];
      });
    },
    linePath() {
      const pts = this.points;
      if (!pts.length) return "";
      return pts
        .map(
          (p, i) =>
            `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`
        )
        .join(" ");
    },
    // The line path closed down to the baseline and back, so the gradient area
    // fills the region beneath the trend line.
    areaPath() {
      const pts = this.points;
      if (!pts.length) return "";
      return (
        this.linePath +
        ` L${this.width} ${this.height} L0 ${this.height} Z`
      );
    },
  },
};
</script>

<style scoped>
.store-spark {
  height: 28px;
  width: 100%;
}
.store-spark__svg {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
